import React, { useMemo, useState } from 'react';
import { Card, Form, Input, Button, Space, Table, message } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { PlusOutlined } from '@ant-design/icons';

import { useOperatorData } from '@contexts/operator/OperatorDataContext';
import { useOperatorProductService } from '@hooks/operator/products/useOperatorProductService';
import { useInventoryAttributeDefinitions } from '@hooks/orders/useInventoryAttributeDefinitions';
import MerchantSelect from '@components/operator/MerchantSelect';
import useSmartBack from '@hooks/shared/useSmartBack';
import FloatingActionBar, { FloatingActionBarSpacer } from '@components/shared/ui/FloatingActionBar';
import PageHeader from '@shared/PageHeader';
import InventorySelectorModal from '@components/shared/selectors/InventorySelectorModal';
import TransformationItemModal from './components/TransformationItemModal';
import { getTransformationItemColumns } from './columns';
import { normalizeSelectorInventory } from './utils';
import { inventoryTransformationService } from '@services/operator/inventoryTransformationService';

function InventoryTransformationNewContent() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const goBack = useSmartBack();
  const { merchantId } = useOperatorData();
  const productService = useOperatorProductService();
  const attributeDefinitions = useInventoryAttributeDefinitions('operator', merchantId);
  const attributeLabelByName = useMemo(
    () => Object.fromEntries(attributeDefinitions.map(def => [def.name, def.label || def.name])),
    [attributeDefinitions],
  );
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  // 區分「儲存並新增下一筆」的 loading，避免兩顆按鈕同時轉 spinner
  const [savingAndAdding, setSavingAndAdding] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [items, setItems] = useState([]);

  const [selectorVisible, setSelectorVisible] = useState(false);
  const [itemModalVisible, setItemModalVisible] = useState(false);
  const [selectedInventory, setSelectedInventory] = useState(null);

  const handleSuggestName = async () => {
    try {
      setSuggesting(true);
      const res = await inventoryTransformationService.suggestName(merchantId);
      if (res.success) {
        form.setFieldsValue({ name: res.data.name });
      } else {
        message.error(res.message || t('common.message.operation_failed'));
      }
    } catch (error) {
      console.error(error);
    } finally {
      setSuggesting(false);
    }
  };

  const handleSelectInventory = (inventory) => {
    setSelectedInventory(normalizeSelectorInventory(inventory));
    setSelectorVisible(false);
    setItemModalVisible(true);
  };

  const handleConfirmItem = (itemDataOrArray) => {
    const newItems = Array.isArray(itemDataOrArray) ? itemDataOrArray : [itemDataOrArray];
    const now = Date.now();
    setItems(prev => [...prev, ...newItems.map((item, idx) => ({ ...item, tempId: `${now}-${idx}` }))]);
    setItemModalVisible(false);
    setSelectedInventory(null);
  };

  const handleRemoveItem = (index) => {
    const newItems = [...items];
    newItems.splice(index, 1);
    setItems(newItems);
  };

  const handleNoteChange = (index, value) => {
    setItems(prev => prev.map((item, i) => i === index ? { ...item, note: value } : item));
  };

  // 核心提交邏輯：驗證並呼叫 service.create
  const submitValues = async () => {
    const values = await form.validateFields();
    if (items.length === 0) {
      message.error(t('shared.inventory_transformation.error.no_items'));
      return { __aborted: true };
    }

    const payload = {
      ...values,
      items: items.map(item => ({
        inventory_id: item.inventory_id,
        shelf_id: item.shelf_id,
        quantity: item.quantity,
        reason_code: item.reason_code,
        target_batch: item.target_batch,
        target_expiration_date: item.target_expiration_date,
        target_manufacturing_date: item.target_manufacturing_date,
        target_inventory_type_id: item.target_inventory_type_id,
        target_custom_attributes: item.target_custom_attributes,
        note: item.note
      }))
    };

    return await inventoryTransformationService.create(payload);
  };

  // 主要提交：成功後導向該筆記錄的 show 頁
  const handleSubmit = async () => {
    setLoading(true);
    try {
      const response = await submitValues();
      if (response?.__aborted) return;

      if (response.success) {
        message.success(t('common.message.create_success'));
        // 導向該筆記錄的 show 頁，避免返回快取的列表頁看不到新資料
        const recordId = response.data?.id;
        navigate(`/inventory_transformations/${recordId}`, { replace: true });
      } else {
        message.error(response.message || t('common.message.operation_failed'));
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // 儲存並新增下一筆：成功後重置表單（包含 items 清單）留在本頁
  const handleSaveAndAddAnother = async () => {
    setSavingAndAdding(true);
    try {
      const response = await submitValues();
      if (response?.__aborted) return;

      if (response.success) {
        message.success(t('common.message.create_success'));
        // 重置 antd Form 欄位
        form.resetFields();
        // 清空表單外的動態 items 狀態
        setItems([]);
      } else {
        message.error(response.message || t('common.message.operation_failed'));
      }
    } catch (error) {
      console.error(error);
    } finally {
      setSavingAndAdding(false);
    }
  };

  const columns = getTransformationItemColumns(t, { onRemove: handleRemoveItem, onNoteChange: handleNoteChange, attributeLabelByName });

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto' }}>
      <PageHeader items={['stocks', 'stocks.transformations', { label: t('shared.inventory_transformation.new_title') }]} />

      <Card title={t('shared.inventory_transformation.basic_info')} bordered={false} style={{ marginBottom: 16 }}>
        <Form form={form} layout="inline">
          <Form.Item label={t('models.inventory_transformation.name')}>
            <Space.Compact style={{ width: 320 }}>
              <Form.Item name="name" noStyle>
                <Input placeholder={t('shared.inventory_transformation.name_placeholder')} />
              </Form.Item>
              <Button
                onClick={handleSuggestName}
                loading={suggesting}
                disabled={loading || savingAndAdding}
              >
                {t('shared.inventory_transformation.suggest_name')}
              </Button>
            </Space.Compact>
          </Form.Item>
          <Form.Item label={t('models.inventory_transformation.note')} name="note" style={{ flex: 1 }}>
            <Input.TextArea rows={1} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Card>

      <Card
        title={t('shared.inventory_transformation.items')}
        bordered={false}
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setSelectorVisible(true)}
          >
            {t('common.button.add')}
          </Button>
        }
      >
        <Table
          columns={columns}
          dataSource={items}
          rowKey="tempId"
          pagination={false}
          size="small"
          scroll={{ x: 'max-content' }}
          locale={{ emptyText: t('shared.inventory_transformation.no_items') }}
        />
      </Card>

      <FloatingActionBarSpacer />
      <FloatingActionBar
        role="operator"
        onCancel={goBack}
        onSubmit={handleSubmit}
        loading={loading}
        disabled={savingAndAdding}
        extraButtons={(
          <Button
            loading={savingAndAdding}
            disabled={loading}
            onClick={handleSaveAndAddAnother}
          >
            {t('common.button.save_and_add_another')}
          </Button>
        )}
        submitText={t('common.button.submit')}
      />

      <InventorySelectorModal
        visible={selectorVisible}
        onCancel={() => setSelectorVisible(false)}
        onSelect={handleSelectInventory}
        merchantId={merchantId}
        productService={productService}
      />

      <TransformationItemModal
        visible={itemModalVisible}
        inventory={selectedInventory}
        existingItems={items}
        defaultReasonCode={items.length > 0 ? items[items.length - 1].reason_code : undefined}
        onCancel={() => setItemModalVisible(false)}
        onConfirm={handleConfirmItem}
      />
    </div>
  );
}

export default function InventoryTransformationNew() {
  return (
    <MerchantSelect>
      <InventoryTransformationNewContent />
    </MerchantSelect>
  );
}
